import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, CircleAlert, ExternalLink, Loader2, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { api } from '../../lib/api';
import type {
  CloudflareDomainStatus,
  DomainCheckResult,
  DomainConnectResult,
  DomainSearchResult,
  RedirectPresetState,
} from '../../lib/types';
import { TutorialChecklist, type StepState, type TutorialStep } from '../../components/ui/TutorialChecklist';

interface Props {
  isSaasAdmin: boolean;
}

const EMPTY_REDIRECTS: RedirectPresetState = {
  wwwToApex: false,
  apexToWww: false,
  pagesDevToPrimary: false,
  status: 'idle',
  message: null,
  lastAppliedAt: null,
  listId: null,
  ruleId: null,
};

function StatusPill({ tone, children }: { tone: 'good' | 'warn' | 'muted'; children: React.ReactNode }) {
  const cls = tone === 'good'
    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
    : tone === 'warn'
      ? 'bg-amber-500/10 text-amber-300 border-amber-500/20'
      : 'bg-ui-bg text-ui-text-muted border-ui-border';
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs ${cls}`}>{children}</span>;
}

function money(currency: string, value: string | null) {
  if (!value) return 'Sin precio disponible';
  return `${currency} ${value}`;
}

function toTransferSteps(steps: Array<{ id: string; title: string; detail: string }>): TutorialStep[] {
  return steps.map((step) => ({
    id: step.id,
    title: step.title,
    body: <p>{step.detail}</p>,
    doneLabel: 'Listo',
  }));
}

function humanizeDomainStatus(raw: string | null | undefined): { label: string; tone: 'good' | 'warn' | 'muted' } {
  switch (raw) {
    case 'active': return { label: 'en línea', tone: 'good' };
    case 'pending': return { label: 'en proceso', tone: 'warn' };
    case 'blocked': return { label: 'bloqueado', tone: 'warn' };
    default: return { label: 'sin conectar', tone: 'muted' };
  }
}

export default function DomainsSettingsTab({ isSaasAdmin }: Props) {
  const [status, setStatus] = useState<CloudflareDomainStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showCustomDomainTools, setShowCustomDomainTools] = useState(false);

  // Ops-only form state
  const [accountId, setAccountId] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [primaryDomainConfig, setPrimaryDomainConfig] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DomainSearchResult[]>([]);
  const [searchPicked, setSearchPicked] = useState<string>('');
  const [checkResult, setCheckResult] = useState<DomainCheckResult | null>(null);

  const [connectDomain, setConnectDomain] = useState('');
  const [connectResult, setConnectResult] = useState<DomainConnectResult | null>(null);
  const [redirects, setRedirects] = useState<RedirectPresetState>(EMPTY_REDIRECTS);
  const [transferGuide, setTransferGuide] = useState<Array<{ id: string; title: string; detail: string }>>([]);
  const [transferState, setTransferState] = useState<Record<string, StepState>>({});

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusData, guideData] = await Promise.all([
        api.domains.status(),
        api.domains.transferGuide(),
      ]);
      setStatus(statusData);
      setAccountId(statusData.accountId ?? '');
      setPrimaryDomainConfig(statusData.primaryDomain ?? '');
      setApiToken('');
      setRedirects({ ...EMPTY_REDIRECTS, ...statusData.redirects });
      setTransferState(statusData.transferState);
      setConnectDomain(statusData.primaryDomain ?? '');
      setTransferGuide(guideData.steps);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el módulo de dominios.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!status) return;
    if (status.accountId || status.pagesDomain || status.domainKind === 'apex') {
      setShowCustomDomainTools(true);
    }
  }, [status]);

  const capabilityItems = useMemo(() => {
    if (!status) return [];
    return [
      { label: 'Registrar', ok: status.capabilities.registrar },
      { label: 'Pages', ok: status.capabilities.pages },
      { label: 'DNS / zonas', ok: status.capabilities.zoneRead },
      { label: 'Redirects', ok: status.capabilities.bulkRedirects },
    ];
  }, [status]);

  async function saveConfig() {
    setBusy('config');
    setError(null);
    setNotice(null);
    try {
      await api.domains.updateConfig({
        accountId,
        primaryDomain: primaryDomainConfig,
        ...(apiToken !== '' && { apiToken }),
      });
      setNotice('La configuración del módulo quedó guardada.');
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la configuración.');
    } finally {
      setBusy(null);
    }
  }

  async function runSearch() {
    setBusy('search');
    setError(null);
    setNotice(null);
    try {
      const result = await api.domains.search(searchQuery);
      setSearchResults(result.results);
      setCheckResult(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo buscar dominios.');
    } finally {
      setBusy(null);
    }
  }

  async function runCheck(domain: string) {
    setBusy('check');
    setError(null);
    setNotice(null);
    try {
      const result = await api.domains.check(domain);
      setCheckResult(result);
      setSearchPicked(domain);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo revisar ese dominio.');
    } finally {
      setBusy(null);
    }
  }

  async function runRegister() {
    if (!searchPicked) return;
    setBusy('register');
    setError(null);
    setNotice(null);
    try {
      const result = await api.domains.register(searchPicked);
      setNotice(result.message);
      setPrimaryDomainConfig(result.domain);
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo iniciar la compra.');
    } finally {
      setBusy(null);
    }
  }

  async function runConnect() {
    setBusy('connect');
    setError(null);
    setNotice(null);
    try {
      const result = await api.domains.connect(connectDomain);
      setConnectResult(result);
      setNotice(result.message);
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo conectar el dominio.');
    } finally {
      setBusy(null);
    }
  }

  async function runRefresh() {
    setBusy('refresh');
    setError(null);
    setNotice(null);
    try {
      const result = await api.domains.refresh();
      setNotice(result.message);
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo revisar de nuevo el estado.');
    } finally {
      setBusy(null);
    }
  }

  async function saveRedirects(next: RedirectPresetState) {
    setBusy('redirects');
    setError(null);
    setNotice(null);
    try {
      const saved = await api.domains.updateRedirects(next);
      setRedirects(saved);
      setNotice(saved.message ?? 'Las redirecciones quedaron actualizadas.');
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron guardar las redirecciones.');
    } finally {
      setBusy(null);
    }
  }

  function toggleRedirect(key: keyof RedirectPresetState) {
    if (!status) return;
    const next = { ...redirects, [key]: !redirects[key] } as RedirectPresetState;
    if (key === 'wwwToApex' && next.wwwToApex) next.apexToWww = false;
    if (key === 'apexToWww' && next.apexToWww) next.wwwToApex = false;
    setRedirects(next);
    void saveRedirects(next);
  }

  const handleTransferStateChange = useCallback((next: Record<string, StepState>) => {
    setTransferState(next);
    void api.domains.updateConfig({ transferState: next }).catch(() => {});
  }, []);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-ui-text-muted"><Loader2 size={16} className="animate-spin" /> Cargando dominios…</div>;
  }

  const domainStatus = humanizeDomainStatus(status?.pagesDomain?.status);
  const customDomainAutomationReady = Boolean(status?.accountId && status?.apiTokenConfigured);
  const currentHostname = status?.primaryDomain || 'Aún no definida';
  const customDomainMode = Boolean(status?.primaryDomain && status.domainKind === 'apex');
  const currentDomainHelp = customDomainMode
    ? 'Este ya es tu dominio propio principal. Si quieres cambiarlo o automatizar redirects, usa las opciones avanzadas de abajo.'
    : 'Esta es la dirección base actual de tu sitio. Si te quedas con ella, no necesitas guardar credenciales de Cloudflare.';
  const customDomainIssues = status?.issues.filter((issue) =>
    issue.includes('Account ID')
    || issue.includes('token')
    || issue.includes('Cloudflare')
    || issue.includes('Pages')
  ) ?? [];
  const generalIssues = status?.issues.filter((issue) => !customDomainIssues.includes(issue)) ?? [];

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          <CircleAlert size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="flex gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      {/* ── Estado actual ─────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-ui-border bg-ui-surface p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-ui-text">Tu dirección actual</h3>
            <p className="text-sm text-ui-text-subtle">Empieza por aquí. Este bloque te dice qué hostname está sirviendo hoy tu sitio.</p>
          </div>
          <button onClick={runRefresh} disabled={busy === 'refresh'} className="inline-flex items-center gap-2 rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text hover:border-ui-accent disabled:opacity-50">
            {busy === 'refresh' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Revisar de nuevo
          </button>
        </div>

        <div className={`grid gap-4 ${isSaasAdmin ? 'md:grid-cols-2' : ''}`}>
          <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-2">
            <p className="text-xs uppercase tracking-wide text-ui-text-subtle">
              {customDomainMode ? 'Dominio propio activo' : 'Subdominio / dirección actual'}
            </p>
            <p className="text-lg text-ui-text">{currentHostname}</p>
            <div className="flex flex-wrap gap-2">
              <StatusPill tone={domainStatus.tone}>{domainStatus.label}</StatusPill>
              {!customDomainMode && <StatusPill tone="muted">No requiere Cloudflare manual</StatusPill>}
            </div>
            <p className="text-sm text-ui-text-subtle">{currentDomainHelp}</p>
          </div>

          {/* Solo ops: capacidades técnicas de Cloudflare */}
          {isSaasAdmin && (
            <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-2">
              <div className="flex items-center gap-1.5 mb-1">
                <ShieldCheck size={13} className="text-ui-text-subtle" />
                <p className="text-xs uppercase tracking-wide text-ui-text-subtle">Capacidades detectadas</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {capabilityItems.map((item) => (
                  <StatusPill key={item.label} tone={item.ok ? 'good' : 'warn'}>{item.label}</StatusPill>
                ))}
              </div>
              <p className="text-sm text-ui-text-subtle">
                Cuenta: {status?.accountId
                  ? `${status.accountId} (${status.accountIdSource === 'domain' ? 'módulo dominios' : 'reutilizada de publish'})`
                  : 'sin configurar'}
              </p>
              <p className="text-sm text-ui-text-subtle">Pages project: {status?.pagesProject || 'pendiente en la tab Sitio'}</p>
            </div>
          )}
        </div>

        {generalIssues.length ? (
          <div className="space-y-1">
            {generalIssues.map((issue) => <p key={issue} className="text-sm text-ui-text-subtle">• {issue}</p>)}
          </div>
        ) : null}

        {status?.pagesDomain?.validation_data?.txt_name && (
          <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-2">
            <p className="text-sm text-ui-text">Validación pendiente</p>
            <p className="text-sm text-ui-text-subtle">Crea estos registros en tu proveedor de dominio y luego pulsa "Revisar de nuevo".</p>
            <div className="grid gap-2 md:grid-cols-2">
              <div>
                <p className="text-xs text-ui-text-subtle mb-1">TXT nombre</p>
                <div className="rounded border border-ui-border px-3 py-2 font-mono text-xs text-ui-text">{status.pagesDomain.validation_data.txt_name}</div>
              </div>
              <div>
                <p className="text-xs text-ui-text-subtle mb-1">TXT valor</p>
                <div className="rounded border border-ui-border px-3 py-2 font-mono text-xs text-ui-text">{status.pagesDomain.validation_data.txt_value}</div>
              </div>
            </div>
            {status.pagesHostname && (
              <p className="text-sm text-ui-text-subtle">Para subdominios, el CNAME debe apuntar a <span className="font-mono text-ui-text">{status.pagesHostname}</span>.</p>
            )}
          </div>
        )}
      </section>

      <section className="rounded-xl border border-ui-border bg-ui-surface p-5 space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-ui-text">Dominio propio (opcional)</h3>
            <p className="text-sm text-ui-text-subtle">
              Usa esta sección solo si quieres cambiar de tu dirección actual a un dominio como <span className="font-mono">farmacia.com</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCustomDomainTools((open) => !open)}
            className="inline-flex items-center justify-center rounded-md border border-ui-border px-4 py-2 text-sm text-ui-text hover:border-ui-accent"
          >
            {showCustomDomainTools ? 'Ocultar opciones avanzadas' : 'Quiero usar mi propio dominio'}
          </button>
        </div>

        {!showCustomDomainTools && (
          <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-2">
            <p className="text-sm text-ui-text">
              Puedes seguir usando <span className="font-mono">{currentHostname}</span> sin configurar nada más.
            </p>
            <p className="text-sm text-ui-text-subtle">
              Solo abre las opciones avanzadas si vas a comprar o conectar un dominio propio.
            </p>
          </div>
        )}

        {showCustomDomainTools && (
          <div className="space-y-6">
            <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-2">
              <p className="text-sm text-ui-text">
                Estas opciones son solo para migrar a un dominio propio. El subdominio SaaS actual no depende de este bloque.
              </p>
              {!customDomainAutomationReady && (
                <p className="text-sm text-ui-text-subtle">
                  Antes de automatizar compra o conexión de dominios propios, primero hay que guardar las credenciales de Cloudflare para este tenant.
                </p>
              )}
              {customDomainIssues.length > 0 && (
                <div className="space-y-1">
                  {customDomainIssues.map((issue) => <p key={issue} className="text-sm text-ui-text-subtle">• {issue}</p>)}
                </div>
              )}
            </div>

            {/* ── Configuración interna Cloudflare (solo saas_admin) ───────── */}
            {isSaasAdmin && (
              <section className="rounded-xl border border-ui-border bg-ui-surface p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={15} className="text-ui-text-subtle" />
                  <div>
                    <h3 className="text-sm font-semibold text-ui-text">Acceso Cloudflare para dominio propio</h3>
                    <p className="text-sm text-ui-text-subtle">Solo visible para el equipo interno. Esto no es necesario para seguir usando el subdominio SaaS.</p>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm text-ui-text-muted mb-1">Account ID</label>
                    <input value={accountId} onChange={(e) => setAccountId(e.target.value)} className="w-full rounded border border-ui-border bg-ui-bg px-3 py-2 text-sm text-ui-text font-mono focus:outline-none focus:border-ui-accent" />
                  </div>
                  <div>
                    <label className="block text-sm text-ui-text-muted mb-1">Token del módulo</label>
                    <input type="password" value={apiToken} onChange={(e) => setApiToken(e.target.value)} placeholder={status?.apiTokenConfigured ? '•••••••• (déjalo vacío para no cambiarlo)' : 'Pega el token aquí'} className="w-full rounded border border-ui-border bg-ui-bg px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-ui-text-muted mb-1">Dominio principal</label>
                    <input value={primaryDomainConfig} onChange={(e) => setPrimaryDomainConfig(e.target.value)} placeholder="miempresa.com o www.miempresa.com" className="w-full rounded border border-ui-border bg-ui-bg px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
                  </div>
                </div>
                <button onClick={saveConfig} disabled={busy === 'config'} className="rounded-md bg-ui-accent px-4 py-2 text-sm text-ui-text-inverse disabled:opacity-50">
                  {busy === 'config' ? 'Guardando…' : 'Guardar configuración'}
                </button>
              </section>
            )}

            {/* ── Comprar dominio ─────────────────────────────────────────── */}
            <section className="rounded-xl border border-ui-border bg-ui-surface p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-ui-text">Comprar dominio propio</h3>
                <p className="text-sm text-ui-text-subtle">Primero se sugieren opciones y luego se valida el precio antes de comprar.</p>
              </div>
              <div className="flex gap-2">
                <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="palabra, marca o idea de dominio" className="flex-1 rounded border border-ui-border bg-ui-bg px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
                <button onClick={runSearch} disabled={busy === 'search' || !searchQuery || !status?.capabilities.registrar} className="inline-flex items-center gap-2 rounded-md border border-ui-border px-4 py-2 text-sm text-ui-text disabled:opacity-50">
                  {busy === 'search' ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                  Buscar
                </button>
              </div>
              {!status?.capabilities.registrar && (
                <p className="text-sm text-ui-text-subtle">La compra automática aún no está habilitada para este tenant o falta terminar la configuración de Cloudflare.</p>
              )}
              <div className="space-y-2">
                {searchResults.map((item) => (
                  <div key={item.name} className="flex flex-col gap-3 rounded-lg border border-ui-border bg-ui-bg p-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm text-ui-text">{item.name}</p>
                      <p className="text-xs text-ui-text-subtle">{item.registrable ? `${money(item.currency, item.registrationCost)} el primer año` : (item.reason || 'No disponible')}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => runCheck(item.name)} disabled={busy === 'check'} className="rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text disabled:opacity-50">Validar precio</button>
                      <button onClick={() => { setSearchPicked(item.name); setCheckResult(null); }} className="rounded-md border border-ui-border px-3 py-2 text-sm text-ui-text">Elegir</button>
                    </div>
                  </div>
                ))}
              </div>
              {checkResult && (
                <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-2">
                  <p className="text-sm text-ui-text">{checkResult.domain}</p>
                  <p className="text-sm text-ui-text-subtle">
                    {checkResult.registrable
                      ? `Disponible ahora por ${money(checkResult.currency, checkResult.registrationCost)}`
                      : (checkResult.reason || 'No disponible')}
                  </p>
                  <button onClick={runRegister} disabled={busy === 'register' || !checkResult.registrable} className="rounded-md bg-ui-accent px-4 py-2 text-sm text-ui-text-inverse disabled:opacity-50">
                    {busy === 'register' ? 'Comprando…' : 'Comprar dominio'}
                  </button>
                </div>
              )}
            </section>

            {/* ── Traer mi dominio ───────────────────────────────────────── */}
            <section className="rounded-xl border border-ui-border bg-ui-surface p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-ui-text">Conectar dominio propio</h3>
                <p className="text-sm text-ui-text-subtle">Úsalo si ya tienes un dominio como <span className="font-mono">farmacia.com</span> o <span className="font-mono">www.farmacia.com</span>.</p>
              </div>
              <div className="flex gap-2">
                <input value={connectDomain} onChange={(e) => setConnectDomain(e.target.value)} placeholder="miempresa.com o shop.miempresa.com" className="flex-1 rounded border border-ui-border bg-ui-bg px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
                <button onClick={runConnect} disabled={busy === 'connect' || !connectDomain || !customDomainAutomationReady} className="rounded-md bg-ui-accent px-4 py-2 text-sm text-ui-text-inverse disabled:opacity-50">
                  {busy === 'connect' ? 'Conectando…' : 'Conectar'}
                </button>
              </div>
              {!customDomainAutomationReady && (
                <p className="text-sm text-ui-text-subtle">Primero deben guardarse las credenciales de Cloudflare para automatizar esta conexión.</p>
              )}
              {connectResult && (
                <div className="rounded-lg border border-ui-border bg-ui-bg p-4 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <StatusPill tone={connectResult.automationAvailable ? 'good' : 'warn'}>{connectResult.automationAvailable ? 'Automático' : 'Guiado'}</StatusPill>
                  </div>
                  <p className="text-sm text-ui-text">{connectResult.message}</p>
                  {connectResult.expectedTarget && (
                    <p className="text-sm text-ui-text-subtle">CNAME esperado: <span className="font-mono text-ui-text">{connectResult.expectedTarget}</span></p>
                  )}
                  {connectResult.pagesDomain?.validation_data?.txt_name && (
                    <div className="grid gap-2 md:grid-cols-2">
                      <div>
                        <p className="text-xs text-ui-text-subtle mb-1">TXT nombre</p>
                        <div className="rounded border border-ui-border px-3 py-2 font-mono text-xs text-ui-text">{connectResult.pagesDomain.validation_data.txt_name}</div>
                      </div>
                      <div>
                        <p className="text-xs text-ui-text-subtle mb-1">TXT valor</p>
                        <div className="rounded border border-ui-border px-3 py-2 font-mono text-xs text-ui-text">{connectResult.pagesDomain.validation_data.txt_value}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* ── Redirecciones ──────────────────────────────────────────── */}
            <section className="rounded-xl border border-ui-border bg-ui-surface p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-ui-text">Redirecciones</h3>
                <p className="text-sm text-ui-text-subtle">Activa los desvíos básicos para que todos los visitantes lleguen a la misma versión del sitio.</p>
              </div>
              {[
                { key: 'wwwToApex', label: 'www → dirección principal sin www' },
                { key: 'apexToWww', label: 'dirección principal → www' },
                { key: 'pagesDevToPrimary', label: 'dirección temporal → dirección principal' },
              ].map((item) => (
                <label key={item.key} className="flex items-center justify-between gap-3 rounded-lg border border-ui-border bg-ui-bg px-4 py-3">
                  <span className="text-sm text-ui-text">{item.label}</span>
                  <input
                    type="checkbox"
                    checked={Boolean(redirects[item.key as keyof RedirectPresetState])}
                    onChange={() => toggleRedirect(item.key as keyof RedirectPresetState)}
                    disabled={busy === 'redirects' || !status?.primaryDomain}
                    className="rounded border-ui-border"
                  />
                </label>
              ))}
              {redirects.message && <p className="text-sm text-ui-text-subtle">{redirects.message}</p>}
            </section>

            {/* ── Transferir dominio ─────────────────────────────────────── */}
            <section className="space-y-4">
              <TutorialChecklist
                title="Mover mi dominio a esta cuenta"
                subtitle="Este proceso sigue siendo paso a paso. Aquí guardamos tu avance y el orden correcto para que no pierdas el progreso."
                steps={toTransferSteps(transferGuide)}
                initialState={transferState}
                onStateChange={handleTransferStateChange}
                completionTitle="Proceso completado"
                completionBody={<span>Cuando todos los pasos estén listos, el dominio debería quedar activo sin que el sitio deje de funcionar.</span>}
              />
              <a href="https://developers.cloudflare.com/registrar/get-started/transfer-domain-to-cloudflare/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-ui-accent hover:opacity-80">
                Ver guía oficial <ExternalLink size={14} />
              </a>
            </section>
          </div>
        )}
      </section>
    </div>
  );
}
